import { ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedPath, protectedRoutes } from '@/config/routes';

import { LoadingSpinner } from '@/components/LoadingSpinner';

import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import ProfileLayout from '@/layouts/ProfileLayout';

import { AdminDashboard } from '@/pages/AdminDashboard';
import { AdminUserDetails } from '@/pages/AdminUserDetails';
import { AuthCallback } from '@/pages/AuthCallback';
import { ClaimsHistory } from '@/pages/ClaimsHistory';
import { Dashboard } from '@/pages/Dashboard';
import { Faq } from '@/pages/Faq';
import { Login } from '@/pages/Login';
import { NotFound } from '@/pages/NotFound';
import { UnleashCategories } from '@/pages/UnleashCategories';

import { filterRoutesByFeatureFlags } from '@/utils/feature-flags';
import { filterRoutesByPermissions } from '@/utils/route-filter';

// Kept apart from `protectedRoutes` so the route config imports no pages.
// Otherwise pages -> Header -> MenuNav -> routes forms an import cycle.
const PAGES: Record<ProtectedPath, ComponentType> = {
  '/dashboard': Dashboard,
  '/profile': ProfileLayout,
  '/faq': Faq,
  '/categories': UnleashCategories,
  '/claims': ClaimsHistory,
  '/admin/dashboard': AdminDashboard,
  '/admin/claims': AdminDashboard,
  '/admin/export': AdminDashboard,
  '/admin/users': AdminDashboard,
  '/admin/users/:userId': AdminUserDetails,
};

function App() {
  const { user, loading } = useAuth();
  const { data: featureFlags, loading: featureFlagsLoading } = useFeatureFlags({
    enabled: Boolean(user),
  });

  if (loading || (user && featureFlagsLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  const allowedRoutes = filterRoutesByFeatureFlags(
    filterRoutesByPermissions(protectedRoutes, user.scope),
    featureFlags,
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {allowedRoutes.map((route) => {
        const PageComponent = PAGES[route.path];

        return <Route key={route.path} path={route.path} element={<PageComponent />} />;
      })}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
